import { useTheme } from '../../hooks/useTheme';

interface ThemePreviewProps {
  className?: string;
}

export function ThemePreview({ className = '' }: ThemePreviewProps) {
  const { theme } = useTheme();

  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-900">Live Preview</h3>
        </div>
      </div>

      <div className="p-5 space-y-6">
        {/* Color Palette */}
        <section>
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
            Color Palette
          </h4>
          <div className="grid grid-cols-4 gap-3">
            <div className="text-center">
              <div
                className="w-full aspect-square rounded-lg shadow-sm mb-1.5"
                style={{ backgroundColor: theme.colors.primary[500] }}
              />
              <span className="text-xs text-gray-600">Primary</span>
            </div>
            <div className="text-center">
              <div
                className="w-full aspect-square rounded-lg shadow-sm mb-1.5"
                style={{ backgroundColor: theme.colors.secondary[500] }}
              />
              <span className="text-xs text-gray-600">Secondary</span>
            </div>
            <div className="text-center">
              <div
                className="w-full aspect-square rounded-lg shadow-sm mb-1.5"
                style={{ backgroundColor: theme.colors.success }}
              />
              <span className="text-xs text-gray-600">Success</span>
            </div>
            <div className="text-center">
              <div
                className="w-full aspect-square rounded-lg shadow-sm mb-1.5"
                style={{ backgroundColor: theme.colors.error }}
              />
              <span className="text-xs text-gray-600">Error</span>
            </div>
          </div>
        </section>

        {/* Typography */}
        <section>
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
            Typography
          </h4>
          <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
            <div
              className="text-gray-900"
              style={{
                fontFamily: theme.typography.fontFamily.sans,
                fontSize: theme.typography.fontSize['2xl'],
                fontWeight: theme.typography.fontWeight.bold,
              }}
            >
              Heading
            </div>
            <div
              className="text-gray-700"
              style={{
                fontFamily: theme.typography.fontFamily.sans,
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.normal,
              }}
            >
              Body text with normal weight.
            </div>
            <div
              className="text-gray-500"
              style={{
                fontFamily: theme.typography.fontFamily.sans,
                fontSize: theme.typography.fontSize.sm,
              }}
            >
              Small caption text.
            </div>
          </div>
        </section>

        {/* Buttons */}
        <section>
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
            Buttons
          </h4>
          <div className="flex flex-wrap gap-2">
            <button
              className="px-3 py-1.5 text-sm text-white shadow-sm transition-colors"
              style={{
                backgroundColor: theme.colors.primary[500],
                borderRadius: theme.borderRadius.md,
              }}
            >
              Primary
            </button>
            <button
              className="px-3 py-1.5 text-sm border transition-colors"
              style={{
                borderColor: theme.colors.gray[300],
                color: theme.colors.gray[700],
                borderRadius: theme.borderRadius.md,
              }}
            >
              Secondary
            </button>
            <button
              className="px-3 py-1.5 text-sm text-white shadow-sm transition-colors"
              style={{
                backgroundColor: theme.colors.success,
                borderRadius: theme.borderRadius.md,
              }}
            >
              Success
            </button>
          </div>
        </section>

        {/* Card Preview */}
        <section>
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
            Card Component
          </h4>
          <div
            className="bg-white border"
            style={{
              borderColor: theme.colors.gray[200],
              borderRadius: theme.borderRadius.lg,
              padding: theme.spacing.md,
              boxShadow: theme.shadows.sm,
            }}
          >
            <div
              className="font-medium text-gray-900 mb-1"
              style={{ fontSize: theme.typography.fontSize.base }}
            >
              Card Title
            </div>
            <div className="text-gray-500" style={{ fontSize: theme.typography.fontSize.sm }}>
              Sample card content showing theme styles.
            </div>
          </div>
        </section>

        {/* Form Elements */}
        <section>
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
            Form Elements
          </h4>
          <div className="space-y-2">
            <input
              type="text"
              placeholder="Input field"
              className="w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              style={{
                borderColor: theme.colors.gray[300],
                borderRadius: theme.borderRadius.md,
                fontSize: theme.typography.fontSize.sm,
              }}
            />
            <div className="flex items-center gap-2">
              <div
                className="w-4 h-4 border-2 rounded flex items-center justify-center"
                style={{
                  borderColor: theme.colors.primary[500],
                  backgroundColor: theme.colors.primary[500],
                }}
              >
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <span className="text-sm text-gray-700">Checkbox option</span>
            </div>
          </div>
        </section>

        {/* Status Badges */}
        <section>
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
            Status Badges
          </h4>
          <div className="flex flex-wrap gap-2">
            <span
              className="px-2 py-0.5 text-xs font-medium rounded-full"
              style={{
                backgroundColor: `${theme.colors.success}20`,
                color: theme.colors.success,
              }}
            >
              Active
            </span>
            <span
              className="px-2 py-0.5 text-xs font-medium rounded-full"
              style={{
                backgroundColor: `${theme.colors.warning}20`,
                color: theme.colors.warning,
              }}
            >
              Pending
            </span>
            <span
              className="px-2 py-0.5 text-xs font-medium rounded-full"
              style={{
                backgroundColor: `${theme.colors.error}20`,
                color: theme.colors.error,
              }}
            >
              Error
            </span>
            <span
              className="px-2 py-0.5 text-xs font-medium rounded-full"
              style={{
                backgroundColor: `${theme.colors.info}20`,
                color: theme.colors.info,
              }}
            >
              Info
            </span>
          </div>
        </section>
      </div>
    </div>
  );
}
